import { type ComponentType, useId, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { views } from '@/copy';
import { LifeCycleBadge } from '@/ui/components/LifeCycleBadge';
import { toneClass } from '../lib/metricTone';
import VisualTooltip from './VisualTooltip';
import InlineTableHeader from './InlineTableHeader';
import { ColumnHeaderRow, ShowMoreButton, gridClassFor } from './tableChrome';
import AssistantVisualFrame from './AssistantVisualFrame';
import type { TableModel, TableRowModel, VisualFrameProps } from '../types';

const DEFAULT_VISIBLE_ROWS = 5;
// Single-line rows are ~half as tall, so report tiles can show more of them and
// still fit the aligned min-height.
const DENSE_REPORT_VISIBLE_ROWS = 8;

const nameCaseClass = (preserveNameCase?: boolean) =>
    preserveNameCase ? '' : 'capitalize';

const RowActions = ({
    row,
    onExcludeRow,
}: {
    row: TableRowModel;
    onExcludeRow: (row: TableRowModel) => void;
}) => (
    <div
        // Inert until revealed, or the invisible overlay swallows clicks on
        // the values it covers. `focus-within` too: keyboard users never hover.
        className="absolute inset-y-0 right-0 flex items-center opacity-0 transition-opacity pointer-events-none group-hover/row:pointer-events-auto group-hover/row:opacity-100 group-focus-within/row:pointer-events-auto group-focus-within/row:opacity-100"
    >
        <div
            aria-hidden="true"
            className="w-8 self-stretch bg-gradient-to-r from-transparent to-card"
        />
        <div className="flex h-full items-center gap-0.5 bg-card pr-2">
            <VisualTooltip content={views.assistantVisuals.excludeRow}>
                <button
                    type="button"
                    aria-label={views.assistantVisuals.excludeRowLabel(
                        row.name,
                    )}
                    onClick={() => onExcludeRow(row)}
                    className="rounded-md p-1.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring cursor-pointer"
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </button>
            </VisualTooltip>
        </div>
    </div>
);

const BodyRow = ({
    row,
    nameAsBadge,
    preserveNameCase,
    gridClass,
    onExcludeRow,
}: {
    row: TableRowModel;
    nameAsBadge?: boolean;
    preserveNameCase?: boolean;
    gridClass: string;
    onExcludeRow?: (row: TableRowModel) => void;
}) => (
    <div
        // Named group: chat's AssistantMessage has an ancestor `.group`, so a
        // bare `group-hover` would reveal every row's actions at once.
        className={`${gridClass} group/row relative px-3.5 py-2.5 border-b border-border last:border-b-0`}
    >
        <div className="flex flex-col min-w-0 gap-1.5">
            {row.nameHref ? (
                // Primary color so it reads as a link (vs the plain-name rows).
                <a
                    href={row.nameHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-xs font-medium text-primary truncate hover:underline ${nameCaseClass(preserveNameCase)}`}
                >
                    {row.name}
                </a>
            ) : nameAsBadge ? (
                // Secondary badge pill (TikTok hashtags, per Figma).
                <span
                    className={`self-start inline-flex max-w-full items-center truncate rounded-md bg-secondary px-2 py-0.5 text-xs text-foreground ${nameCaseClass(preserveNameCase)}`}
                >
                    {row.name}
                </span>
            ) : (
                <span
                    className={`text-xs font-medium text-foreground truncate ${nameCaseClass(preserveNameCase)}`}
                >
                    {row.name}
                </span>
            )}
            {row.badge && (
                // The label is the signal; the tooltip has the health-stat
                // detail and the why.
                <VisualTooltip content={row.badge.title}>
                    <span
                        tabIndex={0}
                        className="self-start max-w-full truncate rounded-md bg-green-600/10 px-1.5 py-0.5 text-[11px] font-medium text-green-700 dark:bg-green-400/15 dark:text-green-400"
                    >
                        {row.badge.label}
                    </span>
                </VisualTooltip>
            )}
            {row.subtext && (
                <span className="text-[11px] text-muted-foreground truncate capitalize">
                    {row.subtext}
                </span>
            )}
            {row.lifeCycle && (
                <span className="self-start">
                    <LifeCycleBadge stage={row.lifeCycle} />
                </span>
            )}
        </div>
        {row.cells.map((cell, j) => (
            <span
                key={j}
                className={`text-xs text-right truncate uppercase font-semibold ${toneClass(
                    cell.tone,
                )}`}
            >
                {cell.value ?? '—'}
            </span>
        ))}
        {onExcludeRow && <RowActions row={row} onExcludeRow={onExcludeRow} />}
    </div>
);

const InlineTable = ({
    breadcrumb,
    title,
    link,
    nameColumnHeader,
    columns,
    rows,
    nameAsBadge,
    preserveNameCase,
    maxRows,
    dense = false,
    Frame = AssistantVisualFrame,
    onExcludeRow,
}: TableModel & {
    maxRows?: number;
    dense?: boolean;
    Frame?: ComponentType<VisualFrameProps>;
    onExcludeRow?: (row: TableRowModel) => void;
}) => {
    const [expanded, setExpanded] = useState(false);
    const rowsId = useId();
    // Denser surfaces (report tiles) show more rows for single-line ("skinny")
    // tables so they reach the aligned min-height; rows with extra lines
    // (life-cycle / cluster subtext or a badge) and the assistant keep the default.
    const allRowsDense =
        rows.length > 0 &&
        rows.every((r) => !r.subtext && !r.lifeCycle && !r.badge);
    const effectiveMaxRows =
        maxRows ??
        (dense && allRowsDense
            ? DENSE_REPORT_VISIBLE_ROWS
            : DEFAULT_VISIBLE_ROWS);
    const hiddenCount = rows.length - effectiveMaxRows;
    const visibleRows = expanded ? rows : rows.slice(0, effectiveMaxRows);

    return (
        <Frame breadcrumb={breadcrumb}>
            <div className="bg-card border border-border rounded-xl shadow-xs overflow-hidden">
                <InlineTableHeader
                    title={{
                        preserveMemberCase: preserveNameCase,
                        ...title,
                    }}
                    link={link}
                />
                <ColumnHeaderRow
                    nameColumnHeader={nameColumnHeader}
                    columns={columns}
                />
                {rows.length === 0 ? (
                    <div className="px-3.5 py-4 text-xs text-muted-foreground text-center">
                        {views.assistantVisuals.noRows}
                    </div>
                ) : (
                    <div id={rowsId}>
                        {visibleRows.map((row, i) => (
                            <BodyRow
                                // Position, not name: `_drop_duplicate_rows`
                                // dedupes on (id, name), so two rows with
                                // distinct ids can share a name.
                                key={`${row.index ?? i}-${row.name}`}
                                row={row}
                                nameAsBadge={nameAsBadge}
                                preserveNameCase={preserveNameCase}
                                gridClass={gridClassFor(columns.length)}
                                onExcludeRow={onExcludeRow}
                            />
                        ))}
                    </div>
                )}
                {hiddenCount > 0 && (
                    <ShowMoreButton
                        expanded={expanded}
                        hiddenCount={hiddenCount}
                        controlsId={rowsId}
                        onToggle={() => setExpanded((v) => !v)}
                    />
                )}
            </div>
        </Frame>
    );
};

export default InlineTable;
